import {Button} from '@dropbox/dig-components/buttons';
import {Text} from '@dropbox/dig-components/typography';
import {UIIcon} from '@dropbox/dig-icons';
import {ChevronRightLine} from '@dropbox/dig-icons/assets';
import {LaptopMini} from '@dropbox/dig-illustrations';
import cx from 'classnames';
import type {RoadmapAuditFindingViolation} from 'client/models/RoadmapAuditFindingViolation';
import {EmptyState} from 'components/DSYS/EmptyState';
import type {Column} from 'components/DSYS/Table';
import {Table} from 'components/DSYS/Table';
import {Title} from 'components/DSYS/Title';
import {AuditFindingFlags} from 'views/roadmap/audit/AuditFindingsTable';
import styles from 'views/roadmap/Roadmap.module.css';
import {RoadmapInitiativeLink} from 'views/roadmap/RoadmapInitiativeLink';
import {RoadmapJiraStatus} from 'views/roadmap/RoadmapJiraStatus';
import type {RoadmapTab} from 'views/roadmap/types';

type DashboardDestination = Exclude<RoadmapTab, 'all'>;

export interface PeopleRoadmapDashboardPreview {
  summary: string;
  columns: string[];
  rows: Array<{
    id: string;
    cells: string[];
    violations?: RoadmapAuditFindingViolation[];
  }>;
  statusColumnIndex?: number;
  flagsColumnIndex?: number;
  emptyMessage: string;
}

export interface PeopleRoadmapDashboardData {
  roadmap: PeopleRoadmapDashboardPreview;
  launches: PeopleRoadmapDashboardPreview;
  dependencies: PeopleRoadmapDashboardPreview;
  rollup: PeopleRoadmapDashboardPreview;
  allocations: PeopleRoadmapDashboardPreview;
  audit: PeopleRoadmapDashboardPreview;
}

type DashboardPreviewRow = PeopleRoadmapDashboardPreview['rows'][number];

const DashboardPreviewTable = ({
  preview,
  label,
}: {
  preview: PeopleRoadmapDashboardPreview;
  label: string;
}) => {
  const columns: Column<DashboardPreviewRow>[] = preview.columns.map((_, index) => ({
    type: `dashboard_${index}`,
    minWidth: index === 0 ? 180 : 96,
    cantDisable: true,
  }));
  const columnLabelOverrides = Object.fromEntries(
    preview.columns.map((columnLabel, index) => [`dashboard_${index}`, columnLabel])
  );

  return (
    <Table<DashboardPreviewRow>
      columns={columns}
      data={preview.rows}
      resizable={false}
      headerTextSize="small"
      columnLabelOverrides={columnLabelOverrides}
      scrollAreaLabel={`${label} preview`}
      className={cx(styles.table, styles.dashboardTable, styles.peopleDashboardDsysTable)}
    >
      <Table.Header />
      <Table.Body className={styles.tableBody}>
        {preview.rows.length === 0 ? (
          <Table.Row>
            <Table.Cell colSpan={columns.length}>
              <Text size="small" color="subtle" className={styles.peopleDashboardEmptyText}>
                {preview.emptyMessage}
              </Text>
            </Table.Cell>
          </Table.Row>
        ) : (
          preview.rows.map((row) => (
            <Table.Row key={row.id}>
              {row.cells.map((cell, index) => (
                <Table.Cell key={`${row.id}-${preview.columns[index] ?? index}`}>
                  {preview.statusColumnIndex === index ? (
                    <RoadmapJiraStatus status={cell === '—' ? null : cell} />
                  ) : preview.flagsColumnIndex === index ? (
                    <AuditFindingFlags violations={row.violations ?? []} />
                  ) : index === 0 ? (
                    <RoadmapInitiativeLink title={cell} jiraKey={row.id} lines={1} />
                  ) : (
                    <Text size="small" className={styles.peopleDashboardCellText} title={cell}>
                      {cell}
                    </Text>
                  )}
                </Table.Cell>
              ))}
            </Table.Row>
          ))
        )}
      </Table.Body>
    </Table>
  );
};

const DashboardCardHeader = ({
  title,
  destination,
  summary,
  onNavigate,
}: {
  title: string;
  destination: DashboardDestination;
  summary: string;
  onNavigate: (tab: DashboardDestination) => void;
}) => (
  <div className={styles.peopleDashboardCardHeader}>
    <div className={styles.peopleDashboardCardTitle}>
      <Title size={16}>{title}</Title>
      <Text size="small" color="subtle">
        {summary}
      </Text>
    </div>
    <Button
      variant="transparent"
      size="small"
      aria-label={`View ${title}`}
      withIconEnd={<UIIcon src={ChevronRightLine} size="small" />}
      onClick={() => onNavigate(destination)}
    >
      View
    </Button>
  </div>
);

const DashboardCard = ({
  title,
  destination,
  preview,
  onNavigate,
}: {
  title: string;
  destination: DashboardDestination;
  preview: PeopleRoadmapDashboardPreview;
  onNavigate: (tab: DashboardDestination) => void;
}) => (
  <article className={styles.peopleDashboardCard} aria-label={`${title} summary`}>
    <DashboardCardHeader
      title={title}
      destination={destination}
      summary={preview.summary}
      onNavigate={onNavigate}
    />
    <DashboardPreviewTable preview={preview} label={title} />
  </article>
);

export const AllTab = ({
  data,
  onNavigate,
}: {
  data: PeopleRoadmapDashboardData;
  onNavigate: (tab: DashboardDestination) => void;
}) => {
  const cards: Array<{
    title: string;
    destination: Exclude<DashboardDestination, 'roadmap'>;
    preview: PeopleRoadmapDashboardPreview;
  }> = [
    {title: 'Launches', destination: 'launches', preview: data.launches},
    {title: 'Dependencies', destination: 'dependencies', preview: data.dependencies},
    {title: 'Rollup', destination: 'rollup', preview: data.rollup},
    {title: 'Allocations', destination: 'allocations', preview: data.allocations},
    {title: 'Audit', destination: 'audit', preview: data.audit},
  ];
  const visibleCards = cards.filter(({preview}) => preview.rows.length > 0);
  const hasRoadmapRows = data.roadmap.rows.length > 0;
  const isEmpty = !hasRoadmapRows && visibleCards.length === 0;

  return (
    <section className={styles.peopleDashboard} aria-label="Roadmap dashboard">
      {isEmpty ? (
        <EmptyState
          hideBorder
          marginBottom="0"
          title="No roadmap items"
          body="Roadmap items will appear here when added in Jira."
          image={<LaptopMini width={64} altText="No roadmap items" />}
        />
      ) : (
        <>
          {hasRoadmapRows ? (
            <div className={styles.peopleDashboardRoadmapSection}>
              <DashboardCardHeader
                title="Roadmap"
                destination="roadmap"
                summary={data.roadmap.summary}
                onNavigate={onNavigate}
              />
              <DashboardPreviewTable preview={data.roadmap} label="Roadmap" />
            </div>
          ) : null}

          {visibleCards.length > 0 ? (
            <div className={styles.peopleDashboardCardGrid}>
              {visibleCards.map(({title, destination, preview}) => (
                <DashboardCard
                  key={destination}
                  title={title}
                  destination={destination}
                  preview={preview}
                  onNavigate={onNavigate}
                />
              ))}
            </div>
          ) : null}
        </>
      )}
    </section>
  );
};
